import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import AdminChrome, {
  adminOutlineBtnSx,
  adminSectionTitleSx,
  adminSurfaceCardSx,
} from '../components/AdminChrome';
import { DOCUMENT } from '../constants/documentTheme';

const AdminFeatures = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [features, setFeatures] = useState([]);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/features');
      const list = data.features || [];
      setFeatures(list);
      const next = {};
      list.forEach((f) => {
        next[f.key] = Boolean(f.storedEnabled);
      });
      setDraft(next);
    } catch (err) {
      setError(err.response?.data?.error || t('adminFeatures.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const persistFlags = useCallback(async (nextDraft) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.put('/admin/features', { flags: nextDraft });
      const list = data.features || [];
      setFeatures(list);
      const next = {};
      list.forEach((f) => {
        next[f.key] = Boolean(f.storedEnabled);
      });
      setDraft(next);
      setNotice(t('adminFeatures.saved'));
    } catch (err) {
      setError(err.response?.data?.error || t('adminFeatures.saveError'));
      // Reload authoritative state after a failed optimistic flip
      await load();
    } finally {
      setSaving(false);
    }
  }, [load, t]);

  const toggleFeature = (key, enabled) => {
    const nextDraft = { ...draft, [key]: enabled };
    setDraft(nextDraft);
    persistFlags(nextDraft);
  };

  return (
    <AdminChrome
      title={t('adminFeatures.title')}
      subtitle={t('adminFeatures.subtitle')}
    >
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setNotice('')}>{notice}</Alert> : null}

      <Typography sx={{ ...adminSectionTitleSx, mb: 1 }}>{t('adminFeatures.sectionTitle')}</Typography>
      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 3, maxWidth: 640 }}>
        {t('adminFeatures.sectionHint')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 720 }}>
          {features.map((feature) => (
            <Card
              key={feature.key}
              elevation={0}
              sx={{ ...adminSurfaceCardSx, p: 2.5 }}
              data-testid={`admin-feature-${feature.key}`}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: '1.2rem', color: DOCUMENT.ink }}>
                    {t(`adminFeatures.flags.${feature.key}.label`, { defaultValue: feature.label })}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5, lineHeight: 1.55 }}>
                    {t(`adminFeatures.flags.${feature.key}.description`, {
                      defaultValue: feature.description,
                    })}
                  </Typography>
                  {!feature.envAllowed ? (
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.oxblood, mt: 1 }}>
                      {t('adminFeatures.envDisabled', { env: feature.envKillSwitch })}
                    </Typography>
                  ) : null}
                  {feature.href ? (
                    <Button
                      component={RouterLink}
                      to={feature.href}
                      sx={{ ...adminOutlineBtnSx, mt: 1.5 }}
                      disabled={!draft[feature.key] || !feature.envAllowed}
                    >
                      {t('adminFeatures.openWorkspace')}
                    </Button>
                  ) : null}
                </Box>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft[feature.key])}
                      disabled={!feature.envAllowed || saving}
                      onChange={(e) => toggleFeature(feature.key, e.target.checked)}
                      inputProps={{ 'data-testid': `feature-toggle-${feature.key}` }}
                    />
                  )}
                  label={saving
                    ? t('adminFeatures.saving')
                    : (draft[feature.key] ? t('adminFeatures.on') : t('adminFeatures.off'))}
                  sx={{ ml: 0 }}
                />
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </AdminChrome>
  );
};

export default AdminFeatures;
